import React from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import toast from 'react-hot-toast';

import { useCurrencyContext } from '@/hooks/useCurrencyContext';
import { CURRENCY_LABELS } from '@/lib/currency';

interface CurrencySelectProps {
  /** Optional helper shown on hover when unlocked (e.g. embed: "Currency for credit top-ups"). */
  hint?: string;
  className?: string;
}

export const CurrencySelect: React.FC<CurrencySelectProps> = ({ hint, className }) => {
  const { context, setPreferredCurrency } = useCurrencyContext();

  if (context.availableCurrencies.length <= 1) return null;

  // Message shown on hover only (no text under the control).
  const tooltip = context.locked ? 'Set by your payment currency' : hint;

  const select = (
    <Select
      value={context.displayCurrency}
      onValueChange={(value) => {
        if (context.locked) return;
        void setPreferredCurrency(value).catch(() => {
          toast.error('Could not save currency preference');
        });
      }}
      disabled={context.locked}
    >
      <SelectTrigger className="w-[100px] h-8 text-xs">
        <SelectValue placeholder="Currency" />
      </SelectTrigger>
      <SelectContent>
        {context.availableCurrencies.map((code) => (
          <SelectItem key={code} value={code}>
            {CURRENCY_LABELS[code] || code.toUpperCase()}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  if (!tooltip) {
    return <div className={className}>{select}</div>;
  }

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        {/* Wrap in a div so the tooltip still triggers on hover even when the
            Select is disabled (disabled controls don't emit pointer events). */}
        <TooltipTrigger asChild>
          <div className={className}>{select}</div>
        </TooltipTrigger>
        <TooltipContent side="top">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
